import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import {
  LayoutDashboard,
  Plus,
  Settings,
  Bell,
} from "lucide-react";
import { t } from "../i18n";
import { useAuth } from "../hooks/useAuth";
import { getExistingSubscription, serializeSubscription, registerServiceWorker } from "../lib/webPushRegistration";
import { buildLoggedBillingPath, shouldShowLoggedRenewal } from "../lib/billingGuards";

const navItems = [
  { to: "/app", labelKey: "appDashboard", icon: LayoutDashboard, end: true },
  { to: "/app/account", labelKey: "appAccount", icon: Settings },
  { to: "/app/notifications", labelKey: "appNotifications", icon: Bell },
];

export function AppShell() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const user = useQuery(api.users.current, session ? {} : "skip");
  const registerPushToken = useMutation(api.pushTokens.registerPushToken);
  const syncTimezone = useMutation(api.users.syncTimezone);
  const subscriptionStatus = useQuery(
    api.monitors.getUserSubscriptionStatus,
    session ? {} : "skip",
  );
  const isSubscriptionLoading = session ? subscriptionStatus === undefined : false;
  const shouldRenew = shouldShowLoggedRenewal(subscriptionStatus);
  useEffect(() => {
    if (!session) return;
    void syncTimezone({ timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
    if (user?.notificationsEnabled) {
      void (async () => {
        await registerServiceWorker();
        const subscription = await getExistingSubscription();
        if (subscription) {
          await registerPushToken({ token: serializeSubscription(subscription), platform: "web" });
        }
      })().catch(() => {});
    }
  }, [registerPushToken, session, syncTimezone, user?.notificationsEnabled]);

  return (
    <div className="app-shell">
      <main className="app-main">
        <div className="app-phone">
          <header className="app-topbar">
            <NavLink to="/app" className="app-brand" aria-label="Stalkie">
              <img src="/landing/icon.png" alt="" />
              <span>Stalkie</span>
            </NavLink>
            <nav className="app-desktop-nav" aria-label={t("appNavigation")}>
              {navItems.map(({ to, labelKey, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) => `app-desktop-nav-link ${isActive ? "is-active" : ""}`}
                >
                  <Icon size={17} />
                  {t(labelKey)}
                </NavLink>
              ))}
            </nav>
            <button
              type="button"
              className="app-topbar-action"
              disabled={isSubscriptionLoading}
              onClick={() => {
                if (isSubscriptionLoading) return;
                navigate(shouldRenew ? buildLoggedBillingPath({ reason: "expired" }) : "/app/add-target");
              }}
              aria-label={t("dashboardAddProfile")}
            >
              <Plus size={20} />
            </button>
          </header>

          <div className="app-main-inner">
            <Outlet />
          </div>

          <nav className="app-mobile-nav">
            {navItems.map(({ to, labelKey, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) => `app-mobile-nav-link ${isActive ? "is-active" : ""}`}
              >
                <Icon size={20} />
                {t(labelKey)}
              </NavLink>
            ))}
          </nav>
        </div>
      </main>
    </div>
  );
}
